'use client'

import { useEffect, useMemo, useState } from 'react'

const PwaInstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null)
    const [isInstalled, setIsInstalled] = useState(false)
    const [isDismissed, setIsDismissed] = useState(false)

    useEffect(() => {
        if (typeof window === 'undefined') return

        const dismissed = window.localStorage.getItem('pwa-install-dismissed')
        if (dismissed === '1') setIsDismissed(true)

        const standaloneDisplay = window.matchMedia(
            '(display-mode: standalone)',
        ).matches
        const iosStandalone = Boolean(window.navigator.standalone)

        if (standaloneDisplay || iosStandalone) {
            setIsInstalled(true)
        }

        const onBeforeInstallPrompt = event => {
            event.preventDefault()
            setDeferredPrompt(event)
        }

        const onAppInstalled = () => {
            setIsInstalled(true)
            setDeferredPrompt(null)
        }

        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
        window.addEventListener('appinstalled', onAppInstalled)

        return () => {
            window.removeEventListener(
                'beforeinstallprompt',
                onBeforeInstallPrompt,
            )
            window.removeEventListener('appinstalled', onAppInstalled)
        }
    }, [])

    const shouldRender = useMemo(() => {
        return !isInstalled && !isDismissed && Boolean(deferredPrompt)
    }, [isInstalled, isDismissed, deferredPrompt])

    const handleInstall = async () => {
        if (!deferredPrompt) return

        deferredPrompt.prompt()
        const choice = await deferredPrompt.userChoice

        if (choice?.outcome === 'accepted') {
            setIsInstalled(true)
        }

        setDeferredPrompt(null)
    }

    const handleDismiss = () => {
        setIsDismissed(true)
        window.localStorage.setItem('pwa-install-dismissed', '1')
    }

    if (!shouldRender) return null

    return (
        <div className="fixed bottom-[92px] right-4 z-[90] w-[calc(100vw-2rem)] max-w-xs rounded-2xl border border-blue-200/70 bg-white/95 p-3 shadow-xl backdrop-blur-md dark:border-blue-500/30 dark:bg-gray-900/95 sm:bottom-4 sm:right-4 sm:w-full">
            <div className="mb-2 flex items-start justify-between gap-2">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-500 dark:text-blue-300">
                        Install App
                    </p>
                    <p className="mt-0.5 text-xs font-semibold text-gray-700 dark:text-gray-100">
                        Add DASURECO Dashboard to your home screen.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label="Dismiss install prompt"
                    className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200">
                    <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="2">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            </div>

            <button
                type="button"
                onClick={handleInstall}
                className="inline-flex w-full items-center justify-center rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700">
                Install Now
            </button>
        </div>
    )
}

export default PwaInstallPrompt
